import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiEye, FiEdit, FiTrash2, FiSave, FiImage, FiShield } = FiIcons;

const AdminDashboard = ({ 
  segnalazioni, 
  users, 
  setCurrentView, 
  setSelectedSegnalazione, 
  updateSegnalazione, 
  deleteSegnalazione 
}) => {
  const { isDark } = useTheme();

  const getStatusColor = (status) => {
    const colors = {
      'In attesa': isDark ? 'bg-yellow-800 text-yellow-200' : 'bg-yellow-100 text-yellow-800',
      'Visionato': isDark ? 'bg-blue-800 text-blue-200' : 'bg-blue-100 text-blue-800',
      'Incarico preso': isDark ? 'bg-green-800 text-green-200' : 'bg-green-100 text-green-800',
      'Incarico non preso': isDark ? 'bg-red-800 text-red-200' : 'bg-red-100 text-red-800',
      'Venduto': isDark ? 'bg-purple-800 text-purple-200' : 'bg-purple-100 text-purple-800',
      'Rifiutato': isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800'
    };
    return colors[status] || (isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800');
  };

  const updateStatus = async (segnalazioneId, newStatus) => {
    await updateSegnalazione(segnalazioneId, { stato_segnalazione: newStatus });
  };

  const updateNote = async (segnalazioneId, note) => {
    await updateSegnalazione(segnalazioneId, { note_admin: note });
  };

  const handleDeleteSegnalazione = async (segnalazioneId) => {
    if (confirm('Sei sicuro di voler eliminare questa segnalazione?')) {
      await deleteSegnalazione(segnalazioneId);
    }
  };

  const editSegnalazione = (segnalazione) => {
    setSelectedSegnalazione(segnalazione);
    setCurrentView('modifica-segnalazione');
  };

  const [editingNotes, setEditingNotes] = React.useState({});

  const handleNoteChange = (segnalazioneId, note) => {
    setEditingNotes(prev => ({
      ...prev,
      [segnalazioneId]: note
    }));
  };

  const saveNote = async (segnalazioneId) => {
    const note = editingNotes[segnalazioneId] || '';
    await updateNote(segnalazioneId, note);
    setEditingNotes(prev => {
      const newState = { ...prev };
      delete newState[segnalazioneId];
      return newState;
    });
  };

  React.useEffect(() => {
    const initialNotes = {};
    segnalazioni.forEach(s => {
      if (s.note_admin) {
        initialNotes[s.id] = s.note_admin;
      }
    });
    setEditingNotes(initialNotes);
  }, [segnalazioni]);

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'} transition-colors`}>
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
          <div>
            <h2 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>
              Gestione Segnalazioni
            </h2>
            <p className={`mt-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Monitora e gestisci tutte le segnalazioni immobiliari
            </p>
          </div>
          <button
            onClick={() => setCurrentView('nuova-segnalazione')}
            className="flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-blue-800 transition-all duration-200 transform hover:scale-105 shadow-lg"
          >
            <SafeIcon icon={FiPlus} className="w-5 h-5" />
            <span>Nuova Segnalazione</span>
          </button>
        </div>

        {/* Ownership Protection Info */}
        <div className={`mb-8 p-4 rounded-lg ${
          isDark ? 'bg-green-900/20 border border-green-600' : 'bg-green-50 border border-green-200'
        }`}>
          <div className="flex items-start space-x-3">
            <SafeIcon icon={FiShield} className={`w-5 h-5 mt-0.5 ${
              isDark ? 'text-green-400' : 'text-green-600'
            }`} />
            <div>
              <h4 className={`font-semibold mb-2 ${isDark ? 'text-green-300' : 'text-green-700'}`}>
                🔒 Protezione Proprietà Segnalazioni
              </h4>
              <p className={`text-sm ${isDark ? 'text-green-200' : 'text-green-600'}`}>
                Quando modifichi una segnalazione, il proprietario originale (segnalatore) viene sempre preservato. 
                Puoi aggiornare tutti i dettagli, lo stato e le note, ma la segnalazione rimarrà associata al segnalatore che l'ha creata.
              </p>
            </div>
          </div>
        </div>

        {/* Segnalazioni Grid */}
        <div className="space-y-6 mb-20">
          {segnalazioni.map(segnalazione => {
            const segnalatore = users.find(u => u.id === segnalazione.user_id);
            const currentNote = editingNotes[segnalazione.id] || segnalazione.note_admin || '';

            return (
              <div
                key={segnalazione.id}
                className={`${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border rounded-2xl shadow-lg overflow-hidden transition-all hover:shadow-xl`}
              >
                <div className="p-6">
                  <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                    {/* Informazioni Immobile */}
                    <div className="space-y-4">
                      <div className="flex items-start justify-between">
                        <h3 className={`font-bold text-xl ${isDark ? 'text-white' : 'text-gray-800'}`}>
                          {segnalazione.indirizzo}
                        </h3>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(segnalazione.stato_segnalazione)}`}>
                          {segnalazione.stato_segnalazione}
                        </span>
                      </div>

                      <div className={`space-y-2 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                        <p>🏠 {segnalazione.tipo_immobile} • {segnalazione.mq} mq</p>
                        <p>🔧 Stato: {segnalazione.stato_immobile}</p>
                        <p>👤 {segnalazione.nome_proprietario}</p>
                        <p>📞 {segnalazione.telefono_proprietario}</p>
                      </div>

                      {/* Foto */}
                      {segnalazione.images && segnalazione.images.length > 0 && (
                        <div className="mt-4">
                          <p className={`text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                            📸 Foto ({segnalazione.images.length})
                          </p>
                          <div className="flex space-x-2">
                            {segnalazione.images.slice(0, 3).map((img, idx) => (
                              <img
                                key={idx}
                                src={img.url}
                                alt={`Foto ${idx + 1}`}
                                className="w-16 h-16 object-cover rounded-lg border-2 border-gray-200"
                              />
                            ))}
                          </div>
                        </div>
                      )}

                      {segnalatore && (
                        <div className={`pt-4 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                          <div className="flex items-center space-x-2 mb-1">
                            <SafeIcon icon={FiShield} className={`w-4 h-4 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                            <p className={`text-sm font-semibold ${isDark ? 'text-green-300' : 'text-green-700'}`}>
                              Proprietario Protetto:
                            </p>
                          </div>
                          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            Segnalato da: <span className="font-medium">{segnalatore.email}</span>
                          </p>
                          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            Tel: {segnalatore.phone_number}
                          </p>
                          <p className={`text-xs ${isDark ? 'text-green-400' : 'text-green-600'} mt-1`}>
                            ✓ Ownership preservato durante le modifiche
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Gestione Stato e Azioni */}
                    <div className="space-y-4">
                      <div>
                        <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          Stato Segnalazione
                        </label>
                        <select
                          value={segnalazione.stato_segnalazione}
                          onChange={(e) => updateStatus(segnalazione.id, e.target.value)}
                          className={`w-full px-3 py-2 rounded-lg border-2 text-sm font-medium transition-colors ${
                            isDark 
                              ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' 
                              : 'bg-gray-50 border-gray-200 text-gray-900 focus:border-blue-500'
                          } ${getStatusColor(segnalazione.stato_segnalazione)}`}
                        >
                          <option value="In attesa">⏳ In attesa</option>
                          <option value="Visionato">👁️ Visionato</option>
                          <option value="Incarico preso">✅ Incarico preso</option>
                          <option value="Incarico non preso">❌ Incarico non preso</option>
                          <option value="Venduto">💰 Venduto</option>
                          <option value="Rifiutato">🚫 Rifiutato</option>
                        </select>
                      </div>

                      {/* Azioni Amministratore */}
                      <div className="space-y-3">
                        <h4 className={`text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          🛠️ Azioni Amministratore
                        </h4>
                        <div className="grid grid-cols-1 gap-2">
                          <button
                            onClick={() => setSelectedSegnalazione(segnalazione)}
                            className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors w-full ${
                              isDark 
                                ? 'text-blue-400 hover:bg-gray-700 border border-blue-400/30' 
                                : 'text-blue-600 hover:bg-blue-50 border border-blue-200'
                            }`}
                          >
                            <SafeIcon icon={FiEye} className="w-4 h-4" />
                            <span>Visualizza Dettagli</span>
                          </button>

                          <button
                            onClick={() => editSegnalazione(segnalazione)}
                            className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors w-full ${
                              isDark 
                                ? 'text-green-400 hover:bg-gray-700 border border-green-400/30' 
                                : 'text-green-600 hover:bg-green-50 border border-green-200'
                            }`}
                          >
                            <SafeIcon icon={FiEdit} className="w-4 h-4" />
                            <span>Modifica (Proprietario Protetto)</span>
                          </button>

                          <button
                            onClick={() => handleDeleteSegnalazione(segnalazione.id)}
                            className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors w-full ${
                              isDark 
                                ? 'text-red-400 hover:bg-gray-700 border border-red-400/30' 
                                : 'text-red-600 hover:bg-red-50 border border-red-200'
                            }`}
                          >
                            <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                            <span>Elimina Segnalazione</span>
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Note Amministratore */}
                    <div className="space-y-4">
                      <label className={`block text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        💬 Note per il segnalatore
                      </label>
                      <div className="space-y-3">
                        <textarea
                          value={currentNote}
                          onChange={(e) => handleNoteChange(segnalazione.id, e.target.value)}
                          className={`w-full px-3 py-3 rounded-lg border-2 text-sm transition-colors focus:outline-none ${
                            isDark 
                              ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' 
                              : 'bg-gray-50 border-gray-200 text-gray-900 focus:border-blue-500'
                          }`}
                          rows="3"
                          placeholder="Scrivi un messaggio per aggiornare il segnalatore..."
                        />
                        <button
                          onClick={() => saveNote(segnalazione.id)}
                          className="w-full flex items-center justify-center space-x-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium hover:from-blue-700 hover:to-blue-800 transition-all"
                        >
                          <SafeIcon icon={FiSave} className="w-4 h-4" />
                          <span>Salva Nota</span>
                        </button>
                      </div>
                      <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                        Queste note saranno visibili solo al segnalatore
                      </p>

                      {segnalazione.note_admin && (
                        <div className={`p-3 rounded-lg border-l-4 ${
                          isDark ? 'bg-green-900 border-green-500' : 'bg-green-50 border-green-400'
                        }`}>
                          <p className={`text-xs font-medium mb-1 ${isDark ? 'text-green-300' : 'text-green-700'}`}>
                            ✅ Nota salvata:
                          </p>
                          <p className={`text-sm ${isDark ? 'text-green-200' : 'text-green-800'}`}>
                            {segnalazione.note_admin}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {segnalazione.note && (
                    <div className={`mt-6 pt-4 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                      <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        📝 Note del segnalatore:
                      </label>
                      <p className={`text-sm p-3 rounded-lg ${isDark ? 'text-gray-300 bg-gray-700' : 'text-gray-600 bg-gray-50'}`}>
                        {segnalazione.note}
                      </p>
                    </div>
                  )}

                  <div className={`mt-4 pt-4 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                    <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                      Creata il: {new Date(segnalazione.created_at).toLocaleDateString('it-IT')} alle {new Date(segnalazione.created_at).toLocaleTimeString('it-IT')}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {segnalazioni.length === 0 && (
          <div className="text-center py-20">
            <div className="text-8xl mb-6">🏠</div>
            <h3 className={`text-2xl font-bold mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              Nessuna segnalazione presente
            </h3>
            <p className={`text-lg mb-8 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
              Le nuove segnalazioni degli utenti appariranno qui
            </p>
            <button
              onClick={() => setCurrentView('nuova-segnalazione')}
              className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-8 py-4 rounded-lg font-semibold hover:from-blue-700 hover:to-blue-800 transition-all duration-200 transform hover:scale-105"
            >
              Crea Prima Segnalazione
            </button>
          </div>
        )}

        {/* Footer */}
        <footer className={`py-8 ${isDark ? 'border-gray-700' : 'border-gray-200'} border-t`}>
          <div className={`text-center space-y-3 ${isDark ? 'text-gray-400' : 'text-gray-600'} text-sm leading-relaxed`}>
            <p className="font-semibold">
              HomeScout © 2025 di Dynamic-Web Srls – P.Iva 03034040356 – Tutti i diritti sono riservati
            </p>
            <p>
              Rag. Sociale: DYNAMIC-WEB SRLS - SOCIETA' A RESPONSABILITA' LIMITATA SEMPLIFICATA
            </p>
            <p className="pt-2 border-t border-gray-300 dark:border-gray-600">
              Questa applicazione è stata realizzata da Dynamic-Web Srls e utilizza Supabase per l'archiviazione sicura dei dati.
            </p>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default AdminDashboard;